import { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faCheck, faCalculator, faClonePlus } from '@/fontawesome.ts';
import TradeTypeBadge from '@/components/daytrading/components/TradeTypeBadge.tsx';
import TradeStatusBadge from '@/components/daytrading/components/TradeStatusBadge.tsx';
import MigratedTradeIcon from '@/components/daytrading/components/MigratedTradeIcon.tsx';
import Modal from '@/components/ui/Modal.tsx';
import { formatUSDCentValue, toUSDCentValue } from '@/utils/helpers.ts';
import { handleAPIError } from '@/utils/toasts.ts';
import { APIRoutes } from '@/services/api.routes.ts';
import { calculateDayTrade } from '@/utils/daytrade-calculations.ts';
import { getProfitColorClass } from '@/utils/daytrade-session-profit.ts';
import type {DayTrade, DayTradingSessionState} from '@/models/day-trading-session.ts';
import type { DayTradeAdjustementID, DayTradingSessionID } from '@/models/ids.ts';
import type { DayTradeState, DayTradeStockCountMode } from '@/models/enums.ts';
import type { USDCent } from '@/models/base-types.ts';
import type { DayTradingParticipantState } from '@/models/day-trading-participant.ts';
import {faExternalLinkAlt} from "@/fontawesome.ts";

interface ModeratorTradeCardProps {
  trade: DayTrade;
  sessionId: DayTradingSessionID;
  disabled?: boolean;
  onStateUpdated: (session: DayTradingSessionState) => void;
  budget: USDCent;
  participantState: DayTradingParticipantState | null;
  onOverrideAdjustmentStockCount?: (adjId: DayTradeAdjustementID, count: number | null) => void;
  onToggleStockCountMode?: () => void;
  stockCountMode?: DayTradeStockCountMode;
}

function fmt(n: number, d = 2): string {
  return n.toLocaleString('de-DE', { minimumFractionDigits: d, maximumFractionDigits: d });
}

function fmtShares(n: number): string {
  return parseFloat(n.toFixed(4)).toLocaleString('de-DE', { maximumFractionDigits: 4 });
}

function getCardBorderClass(state: DayTradeState): string {
  switch (state) {
    case 'GOAL_REACHED': return 'border-green-600';
    case 'STOPPED':      return 'border-gray-300 dark:border-gray-700';
    case 'STOPLOSS':     return 'border-red-800';
    default: return 'border-gray-200 dark:border-gray-700';
  }
}

function getContentStateClass(state: DayTradeState): string {
  switch (state) {
    case 'GOAL_REACHED': return 'opacity-75 bg-green-50  dark:bg-green-950/30';
    case 'STOPPED':      return 'opacity-45 bg-gray-50   dark:bg-gray-950/30';
    case 'STOPLOSS':     return 'opacity-75 bg-red-50    dark:bg-red-950/30';
    default:             return '           bg-white     dark:bg-gray-800';
  }
}

export default function ModeratorTradeCard({
  trade,
  sessionId,
  disabled = false,
  onStateUpdated,
  budget,
  participantState,
  onOverrideAdjustmentStockCount,
  onToggleStockCountMode,
  stockCountMode = 'NORMAL',
}: ModeratorTradeCardProps) {

  const calculations = useMemo(() => {
    return calculateDayTrade({
      trade: trade,
      budget: budget,
      mode: 'moderator',
      overrideAdjustementPrices: participantState?.overrideAdjustementPrices ?? {},
      overrideAdjustmentStockCount: participantState?.overrideAdjustmentStockCount ?? {},
      stockCountMode: stockCountMode,
      processedAdjustements: [],
      skippedAdjustements: [],
    });
  }, [trade, budget, participantState?.overrideAdjustementPrices, participantState?.overrideAdjustmentStockCount, stockCountMode]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteAdjId, setDeleteAdjId] = useState<DayTradeAdjustementID | null>(null);
  const [addingEntry, setAddingEntry] = useState(false);
  const [newEntryPrice, setNewEntryPrice] = useState('');
  const [editingTarget, setEditingTarget] = useState(false);
  const [targetInput, setTargetInput] = useState('');
  const [editingShare, setEditingShare] = useState(false);
  const [shareInput, setShareInput] = useState('');
  const [editingAdjId, setEditingAdjId] = useState<DayTradeAdjustementID | null>(null);
  const [adjPriceInput, setAdjPriceInput] = useState('');
  const [exitPriceMode, setExitPriceMode] = useState<'GOAL_REACHED' | 'STOPLOSS' | null>(null);
  const [exitPriceInput, setExitPriceInput] = useState('');
  const [editingStockCountAdjId, setEditingStockCountAdjId] = useState<DayTradeAdjustementID | null>(null);
  const [stockCountInput, setStockCountInput] = useState('');

  const handleStateChange = async (state: DayTradeState, exitPrice?: USDCent | null) => {
    try {
      const response = await APIRoutes.updateDayTrade(sessionId, trade.id, { state, exitPrice });
      onStateUpdated(response.state);
    } catch (err) {
      handleAPIError(err, 'Fehler beim Ändern des Status');
    }
  };

  const handleTargetSave = async () => {
    const val = parseFloat(targetInput.replace(',', '.'));
    if (!isNaN(val) && val > 0) {
      try {
        const response = await APIRoutes.updateDayTrade(sessionId, trade.id, { targetPrice: toUSDCentValue(val) });
        onStateUpdated(response.state);
      } catch (err) {
        handleAPIError(err, 'Fehler beim Speichern des Zielpreises');
      }
    }
    setEditingTarget(false);
  };

  const handleShareSave = async () => {
    const val = parseFloat(shareInput.replace(',', '.'));
    if (!isNaN(val) && val > 0 && val <= 1) {
      try {
        const response = await APIRoutes.updateDayTrade(sessionId, trade.id, { share: val });
        onStateUpdated(response.state);
      } catch (err) {
        handleAPIError(err, 'Fehler beim Speichern des Anteils');
      }
    }
    setEditingShare(false);
  };

  const handleAddAdjustment = async () => {
    const val = parseFloat(newEntryPrice.replace(',', '.'));
    if (!isNaN(val) && val > 0) {
      try {
        const response = await APIRoutes.createDayTradeAdjustement(sessionId, trade.id, { entryPrice: toUSDCentValue(val) });
        onStateUpdated(response.state);

        setNewEntryPrice('');
        setAddingEntry(false);
      } catch (err) {
        handleAPIError(err, 'Fehler beim Hinzufügen des Einstiegs');
      }
    }
  };

  const handleAdjustmentSave = async (adjustmentId: DayTradeAdjustementID) => {
    const val = parseFloat(adjPriceInput.replace(',', '.'));
    if (!isNaN(val) && val > 0) {
      try {
        const response = await APIRoutes.updateDayTradeAdjustement(sessionId, trade.id, adjustmentId, { entryPrice: toUSDCentValue(val) });
        onStateUpdated(response.state);
      } catch (err) {
        handleAPIError(err, 'Fehler beim Speichern des Einstiegspreises');
      }
    }
    setEditingAdjId(null);
  };

  const handleToggleExecuted = async (adjId: DayTradeAdjustementID, currentExecuted: boolean) => {
    try {
      const response = await APIRoutes.updateDayTradeAdjustement(sessionId, trade.id, adjId, { executed: !currentExecuted });
      onStateUpdated(response.state);
    } catch (err) {
      handleAPIError(err, 'Fehler beim Ändern des Ausführungsstatus');
    }
  };

  const handleDeleteAdjustment = async () => {
    if (deleteAdjId === null) return;
    try {
      const response = await APIRoutes.deleteDayTradeAdjustement(sessionId, trade.id, deleteAdjId);
      onStateUpdated(response.state);
    } catch (err) {
      handleAPIError(err, 'Fehler beim Löschen des Einstiegs');
    }
    setDeleteAdjId(null);
  };

  const handleDeleteTrade = async () => {
    try {
      const response = await APIRoutes.deleteDayTrade(sessionId, trade.id);
      onStateUpdated(response.state);
    } catch (err) {
      handleAPIError(err, 'Fehler beim Löschen des Trades');
    }
    setShowDeleteConfirm(false);
  };

  const shareLabel = (() => {
    const inv = 1 / trade.share;
    const rounded = Math.round(inv);
    return `1/${rounded}`;
  })();

  // Calculate "natural" shares (without stock count override) for comparison
  const getNaturalShares = (adjIndex: number): number => {
    if (calculations === null) return 0;
    const adj = trade.adjustments[adjIndex];
    const effectivePrice = participantState?.overrideAdjustementPrices?.[adj.id] ?? adj.entryPrice;
    const entryPriceDollars = effectivePrice / 100;
    let shares = entryPriceDollars > 0 ? Math.floor(calculations.perEntry / entryPriceDollars) : 0;
    if (adjIndex > 0 && stockCountMode === 'FIRST') {
      const firstAdj = trade.adjustments[0];
      const firstPrice = participantState?.overrideAdjustementPrices?.[firstAdj.id] ?? firstAdj.entryPrice;
      const firstPriceDollars = firstPrice / 100;
      shares = firstPriceDollars > 0 ? Math.floor(calculations.perEntry / firstPriceDollars) : 0;
    }
    return shares;
  };

  return (
    <>
      <div className={`relative rounded-2xl border shadow-sm min-w-90 ${getCardBorderClass(trade.state)}`}>

        <div className={`rounded-2xl p-5 transition-opacity h-full flex flex-col ${getContentStateClass(trade.state)} ${trade.migratedFrom !== null ? 'bg-migrated-stripes' : ''}`}>

          {/* Header: Symbol + Badge + Mode Toggle + Delete */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <a
                href={`https://www.tradingview.com/symbols/${trade.symbol}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400 transition-colors mr-2"
                title="Auf TradingView öffnen"
                onClick={(e) => e.stopPropagation()}>
                <FontAwesomeIcon icon={faExternalLinkAlt} className="w-3.5 h-3.5" />
              </a>
              <span className="text-2xl font-extrabold text-gray-900 dark:text-white tracking-tight">{trade.symbol}</span>
              <TradeTypeBadge type={trade.type} />
              {trade.migratedFrom !== null && <MigratedTradeIcon created={trade.created} />}
            </div>
            <div className="flex items-center gap-1">
              {!disabled && onToggleStockCountMode !== undefined && (
                <button
                  onClick={onToggleStockCountMode}
                  className={`text-sm leading-none px-1 cursor-pointer transition-colors ${
                    stockCountMode === 'FIRST'
                      ? 'text-blue-500 dark:text-blue-400'
                      : 'text-gray-500 dark:text-gray-500 hover:text-gray-300'
                  }`}
                  title={stockCountMode === 'NORMAL' ? 'Aktienberechnung: Normal' : 'Aktienberechnung: Erster Einstieg'}
                >
                  <FontAwesomeIcon icon={stockCountMode === 'NORMAL' ? faCalculator : faClonePlus} className="w-4 h-4" />
                </button>
              )}
              {!disabled && (
                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-gray-500 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 cursor-pointer text-sm leading-none px-1"
                  title="Trade löschen"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              )}
            </div>
          </div>

          {/* Investment info line */}
          <div className="text-sm text-gray-500 dark:text-gray-400 mb-3">
            {!disabled && editingShare ? (
              <span className="inline-flex items-center gap-1">
                <input
                  type="number"
                  value={shareInput}
                  onChange={(e) => setShareInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleShareSave(); if (e.key === 'Escape') setEditingShare(false); }}
                  className="w-16 px-1.5 py-0.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  autoFocus
                  step="0.01"
                  min="0.01"
                  max="1"
                />
                <button onClick={handleShareSave} className="text-blue-500 hover:text-blue-400 text-xs font-bold cursor-pointer">OK</button>
                <button onClick={() => setEditingShare(false)} className="text-gray-400 hover:text-gray-300 text-xs cursor-pointer">✕</button>
              </span>
            ) : disabled ? (
              <span>{shareLabel}</span>
            ) : (
              <button
                onClick={() => { setShareInput(trade.share.toString()); setEditingShare(true); }}
                className="hover:text-blue-400 cursor-pointer transition-colors"
              >
                {shareLabel}
              </button>
            )}
            {calculations !== null && (
              <span>
                {' → '}<strong className="text-gray-800 dark:text-gray-200">${fmt(calculations.perEntry)}</strong>{' / Einstieg'}
                {calculations.adjustments.length > 0 && ( <span className="text-gray-500 dark:text-gray-500"> = ${fmt(calculations.totalInvested)} gesamt</span> )}
              </span>
            )}
          </div>

          {/* Einstiege label */}
          <div className="text-[0.7rem] text-gray-500 dark:text-gray-500 uppercase tracking-wider mb-2 font-semibold">
            Einstiege
          </div>

          {/* Adjustment rows */}
          <div className="space-y-1.5 mb-2">
            {trade.adjustments.map((adj, index) => {
              const hasStockCountOverride = participantState?.overrideAdjustmentStockCount?.[adj.id] !== undefined;
              const naturalShares = getNaturalShares(index);

              return (
                <div
                  key={adj.id}
                  className="flex items-center gap-2 bg-gray-100 dark:bg-gray-900/60 rounded-lg px-3 py-2"
                >
                  {!disabled ? (
                    <button
                      onClick={() => handleToggleExecuted(adj.id, adj.executed)}
                      className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 cursor-pointer transition-colors ${
                        adj.executed
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-400 hover:bg-green-200 dark:hover:bg-green-900'
                      }`}
                      title={adj.executed ? 'Als nicht ausgeführt markieren' : 'Als ausgeführt markieren'}
                    >
                      <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.6em' }} />
                    </button>
                  ) : (
                    <div
                      className={`w-5 h-5 rounded-full flex items-center justify-center shrink-0 ${
                        adj.executed
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 dark:bg-gray-600 text-gray-400 dark:text-gray-400'
                      }`}
                      title={adj.executed ? 'Als nicht ausgeführt markieren' : 'Als ausgeführt markieren'}
                    >
                      <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.6em' }} />
                    </div>
                  )}
                  <span className="text-[0.7rem] text-gray-500 dark:text-gray-500 font-semibold w-5 shrink-0">
                    #{index + 1}
                  </span>
                  {!disabled && editingAdjId === adj.id ? (
                    <div className="flex items-center gap-1 flex-1">
                      <input
                        type="number"
                        value={adjPriceInput}
                        onChange={(e) => setAdjPriceInput(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') handleAdjustmentSave(adj.id); if (e.key === 'Escape') setEditingAdjId(null); }}
                        className="w-24 px-2 py-0.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        autoFocus
                        step="0.01"
                      />
                      <button onClick={() => handleAdjustmentSave(adj.id)} className="text-blue-500 hover:text-blue-400 text-xs font-bold cursor-pointer">OK</button>
                      <button onClick={() => setEditingAdjId(null)} className="text-gray-400 hover:text-gray-300 text-xs cursor-pointer">✕</button>
                    </div>
                  ) : disabled ? (
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex-1 text-left">
                      {participantState?.overrideAdjustementPrices?.[adj.id] !== undefined ? (
                        <>
                          <span className="line-through text-gray-400">{formatUSDCentValue(adj.entryPrice)}</span>
                          {' '}
                          <span>{formatUSDCentValue(participantState.overrideAdjustementPrices[adj.id])}</span>
                        </>
                      ) : (
                        formatUSDCentValue(adj.entryPrice)
                      )}
                    </span>
                  ) : (
                    <button
                      onClick={() => { setAdjPriceInput((adj.entryPrice / 100).toString()); setEditingAdjId(adj.id); }}
                      className="text-sm font-semibold text-gray-800 dark:text-gray-200 hover:text-blue-400 dark:hover:text-blue-400 cursor-pointer transition-colors flex-1 text-left"
                    >
                      {participantState?.overrideAdjustementPrices?.[adj.id] !== undefined ? (
                        <>
                          <span className="line-through text-gray-400">{formatUSDCentValue(adj.entryPrice)}</span>
                          {' '}
                          <span>{formatUSDCentValue(participantState.overrideAdjustementPrices[adj.id])}</span>
                        </>
                      ) : (
                        formatUSDCentValue(adj.entryPrice)
                      )}
                    </button>
                  )}
                  {calculations !== null && (
                    <>
                      <span className="text-xs text-gray-500 dark:text-gray-500">
                        ${fmt(calculations.adjustments[index].allocated)}
                      </span>
                      {/* Shares - clickable for override when not disabled */}
                      {!disabled && onOverrideAdjustmentStockCount !== undefined && editingStockCountAdjId === adj.id ? (
                        <div className="flex items-center gap-1 min-w-[68px]">
                          <input
                            type="number"
                            value={stockCountInput}
                            onChange={(e) => setStockCountInput(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                const val = parseInt(stockCountInput);
                                if (!isNaN(val) && val >= 0) {
                                  onOverrideAdjustmentStockCount(adj.id, val === naturalShares ? null : val);
                                }
                                setEditingStockCountAdjId(null);
                              }
                              if (e.key === 'Escape') setEditingStockCountAdjId(null);
                            }}
                            className="w-16 px-1 py-0.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                            autoFocus
                            step="1"
                            min="0"
                          />
                          <button
                            onClick={() => {
                              const val = parseInt(stockCountInput);
                              if (!isNaN(val) && val >= 0) {
                                onOverrideAdjustmentStockCount(adj.id, val === naturalShares ? null : val);
                              }
                              setEditingStockCountAdjId(null);
                            }}
                            className="text-blue-500 hover:text-blue-400 text-xs font-bold cursor-pointer"
                          >
                            OK
                          </button>
                        </div>
                      ) : !disabled && onOverrideAdjustmentStockCount !== undefined ? (
                        <button
                          onClick={() => {
                            setStockCountInput(calculations.adjustments[index].shares.toString());
                            setEditingStockCountAdjId(adj.id);
                          }}
                          className="text-sm font-bold text-purple-400 text-right min-w-[68px] hover:text-blue-400 cursor-pointer transition-colors"
                        >
                          {hasStockCountOverride ? (
                            <>
                              <span className="line-through text-gray-400">{fmtShares(naturalShares)}</span>
                              {' '}
                              <span>{fmtShares(calculations.adjustments[index].shares)}</span>
                            </>
                          ) : (
                            fmtShares(calculations.adjustments[index].shares)
                          )}{' '}Ak.
                        </button>
                      ) : (
                        <span className="text-sm font-bold text-purple-400 text-right min-w-[68px]">
                          {hasStockCountOverride ? (
                            <>
                              <span className="line-through text-gray-400">{fmtShares(naturalShares)}</span>
                              {' '}
                              <span>{fmtShares(calculations.adjustments[index].shares)}</span>
                            </>
                          ) : (
                            fmtShares(calculations.adjustments[index].shares)
                          )}{' '}Ak.
                        </span>
                      )}
                    </>
                  )}
                  {!disabled && trade.adjustments.length > 1 && (
                    <button
                      onClick={() => setDeleteAdjId(adj.id)}
                      className="text-gray-400 dark:text-gray-600 hover:text-red-500 dark:hover:text-red-400 cursor-pointer text-xs shrink-0"
                      title="Einstieg löschen"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          {/* Add entry: dashed button or inline form */}
          {!disabled && (addingEntry ? (
            <div className="flex items-center gap-1.5 mb-4">
              <input
                type="number"
                value={newEntryPrice}
                onChange={(e) => setNewEntryPrice(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddAdjustment();
                  if (e.key === 'Escape') { setAddingEntry(false); setNewEntryPrice(''); }
                }}
                placeholder="Preis $"
                step="0.01"
                autoFocus
                className="flex-1 px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900/60 text-gray-900 dark:text-white placeholder-gray-400"
              />
              <button
                onClick={handleAddAdjustment}
                className="px-2.5 py-1.5 text-xs font-bold rounded-md bg-blue-600 text-white hover:bg-blue-700 cursor-pointer transition-colors"
              >
                OK
              </button>
              <button
                onClick={() => { setAddingEntry(false); setNewEntryPrice(''); }}
                className="px-2 py-1.5 text-xs rounded-md bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600 cursor-pointer transition-colors"
              >
                ✕
              </button>
            </div>
          ) : (
            <button onClick={() => setAddingEntry(true)} className="w-full py-1.5 mb-4 text-xs text-gray-500 dark:text-gray-500 border border-dashed border-gray-300 dark:border-gray-600 rounded-md hover:border-blue-500 hover:text-blue-500 dark:hover:border-blue-500 dark:hover:text-blue-500 cursor-pointer transition-colors">
              + weiterer Einstieg
            </button>
          ))}

          {/* spacer */}
          <div className="grow"></div>

          {/* Summary (moderator-as-participant) */}
          {calculations !== null && trade.adjustments.length > 0 && (
            <div className="bg-gray-100 dark:bg-gray-900/60 rounded-xl p-3 mb-3 space-y-1.5">
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-500 uppercase tracking-wide">Ziel</span>
                {!disabled && editingTarget ? (
                  <span className="inline-flex items-center gap-1">
                    <input
                      type="text"
                      value={targetInput}
                      onChange={(e) => setTargetInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleTargetSave(); if (e.key === 'Escape') setEditingTarget(false); }}
                      className="w-20 px-1.5 py-0.5 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      autoFocus
                      step="0.01"
                    />
                    <button onClick={handleTargetSave} className="text-blue-500 hover:text-blue-400 text-xs font-bold cursor-pointer">OK</button>
                    <button onClick={() => setEditingTarget(false)} className="text-gray-400 hover:text-gray-300 text-xs cursor-pointer">✕</button>
                  </span>
                ) : disabled ? (
                  <span className="font-semibold text-gray-800 dark:text-gray-200">{formatUSDCentValue(trade.targetPrice)}</span>
                ) : (
                  <button
                    onClick={() => { setTargetInput((trade.targetPrice / 100).toString()); setEditingTarget(true); }}
                    className="font-semibold text-gray-800 dark:text-gray-200 hover:text-blue-400 cursor-pointer transition-colors"
                  >{formatUSDCentValue(trade.targetPrice)}</button>
                )}
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-500 uppercase tracking-wide">Aktien gesamt</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">{fmtShares(calculations.totalShares)}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-500 uppercase tracking-wide">Ø Einstieg</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">${fmt(calculations.avgPrice)}</span>
              </div>
              {trade.state !== 'OPEN' && trade.state !== 'STOPPED' && trade.exitPrice !== null && (
                <div className="flex justify-between text-xs">
                  <span className="text-gray-500 dark:text-gray-500 uppercase tracking-wide">Ausstieg</span>
                  <span className="font-semibold text-gray-800 dark:text-gray-200">${formatUSDCentValue(trade.exitPrice)}</span>
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span className="text-gray-500 dark:text-gray-500 uppercase tracking-wide">Kurs &rarr; Ziel</span>
                <span className="font-semibold text-gray-800 dark:text-gray-200">
                  {`Ø $${fmt(calculations.avgPrice)}`}
                  <span className="text-gray-500"> &rarr; </span>
                  <span className="text-purple-400">${fmt(calculations.targetPriceDelta)}</span>
                  <span className="text-gray-500"> = </span>
                  <span className="text-green-500 font-bold">${fmt(calculations.targetPriceAbsolute)}</span>
                </span>
              </div>
            </div>
          )}

          {/* Profit + State Badge (moderator-as-participant) */}
          {calculations !== null && trade.adjustments.length > 0 && (
            <div className="flex items-end justify-between mb-3">
              <div className={`text-xl font-extrabold ${getProfitColorClass(calculations.profit)}`}>
                ${fmt(calculations.profit)}
                <span className="text-sm font-medium opacity-85 ml-1">
                  ({fmt(calculations.profitPct)}%)
                </span>
              </div>
              <TradeStatusBadge state={trade.state} />
            </div>
          )}

          {/* Status actions */}
          {!disabled && trade.state === 'OPEN' && (
            exitPriceMode !== null ? (
              <div className="flex items-center gap-1.5">
                <input
                  type="text"
                  value={exitPriceInput}
                  onChange={(e) => setExitPriceInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const val = parseFloat(exitPriceInput.replace(',', '.'));
                      if (!isNaN(val) && val > 0) {
                        handleStateChange(exitPriceMode, toUSDCentValue(val));
                        setExitPriceMode(null);
                        setExitPriceInput('');
                      }
                    }
                    if (e.key === 'Escape') { setExitPriceMode(null); setExitPriceInput(''); }
                  }}
                  placeholder="Exit Preis $"
                  step="0.01"
                  autoFocus
                  className="flex-1 px-2 py-1.5 text-sm rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900/60 text-gray-900 dark:text-white placeholder-gray-400"
                />
                <button
                  onClick={() => {
                    const val = parseFloat(exitPriceInput.replace(',', '.'));
                    if (!isNaN(val) && val > 0) {
                      handleStateChange(exitPriceMode, toUSDCentValue(val));
                      setExitPriceMode(null);
                      setExitPriceInput('');
                    }
                  }}
                  className="px-2.5 py-1.5 text-xs font-bold rounded-md bg-blue-600 text-white hover:bg-blue-700 cursor-pointer transition-colors"
                >
                  OK
                </button>
                <button
                  onClick={() => { setExitPriceMode(null); setExitPriceInput(''); }}
                  className="px-2 py-1.5 text-xs rounded-md bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-300 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                >
                  ✕
                </button>
              </div>
            ) : (
              <div className="flex gap-1.5">
                <button
                  onClick={() => setExitPriceMode('GOAL_REACHED')}
                  className="flex-1 px-2 py-1.5 text-xs font-semibold rounded-md bg-green-600 text-white hover:bg-green-700 cursor-pointer transition-colors"
                >
                  Ziel erreicht
                </button>
                <button
                  onClick={() => setExitPriceMode('STOPLOSS')}
                  className="flex-1 px-2 py-1.5 text-xs font-semibold rounded-md bg-red-600 text-white hover:bg-red-700 cursor-pointer transition-colors"
                >
                  StopLoss
                </button>
                <button
                  onClick={() => handleStateChange('STOPPED', null)}
                  disabled={trade.adjustments.some((p) => p.executed)}
                  className={`flex-1 px-2 py-1.5 text-xs font-semibold rounded-md bg-gray-500 text-white ${trade.adjustments.some((p) => p.executed) ? "opacity-40" : "hover:bg-gray-600 cursor-pointer"} transition-colors`}
                >
                  Stoppen
                </button>
              </div>
            )
          )}
          {!disabled && (trade.state === 'STOPPED' || trade.state === 'STOPLOSS' || trade.state === 'GOAL_REACHED') && (
            <button onClick={() => handleStateChange('OPEN', null)} className="w-full px-3 py-1.5 text-xs font-semibold rounded-md bg-blue-600 text-white hover:bg-blue-700 cursor-pointer transition-colors">
              Wieder öffnen (zurücksetzen)
            </button>
          )}
        </div>
      </div>

      {/* Delete trade confirmation modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Trade löschen"
        maxWidth="sm"
      >
        <div className="p-6 space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Möchtest du den Trade <strong>{trade.symbol}</strong> wirklich löschen?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleDeleteTrade}
              className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 cursor-pointer transition-colors"
            >
              Löschen
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete adjustment confirmation modal */}
      <Modal
        isOpen={deleteAdjId !== null}
        onClose={() => setDeleteAdjId(null)}
        title="Einstieg löschen"
        maxWidth="sm"
      >
        <div className="p-6 space-y-4">
          <p className="text-gray-700 dark:text-gray-300">
            Möchtest du diesen Einstieg wirklich löschen?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setDeleteAdjId(null)}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer transition-colors"
            >
              Abbrechen
            </button>
            <button
              onClick={handleDeleteAdjustment}
              className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 cursor-pointer transition-colors"
            >
              Löschen
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
}
